import {
  Button,
  Input,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import {
  EyeOffOutlineIcon,
  EyeOutlineIcon,
  Icon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import { IconButton, Typography } from '@material-ui/core';
import cx from 'classnames';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import { messages } from './messages';
import { useStyles } from './styles';
import { OptionBank, QuestionOption } from './types';
import { useMenuEscapeGuard } from './useMenuEscapeGuard';

export interface OptionsEditorProps {
  options: QuestionOption[];
  /** A standard question's options come from its definition and are read-only. */
  readOnly: boolean;
  error?: string;
  optionBanks: OptionBank[];
  optionBankId: number | null | undefined;
  onAddOption: () => void;
  onUpdateOption: (index: number, patch: Partial<QuestionOption>) => void;
  onRemoveOption: (index: number) => void;
  onLinkBank: (id: number) => void;
  onUnlinkBank: () => void;
}

const PREVIEW_LIMIT = 6;

const OptionChips: FC<{
  options: { value: string; label?: string | null }[];
}> = ({ options }) => {
  const styles = useStyles();
  const intl = useIntl();
  const shown = options.slice(0, PREVIEW_LIMIT);
  const hidden = options.length - shown.length;
  return (
    <div className={styles.chips}>
      {shown.map((option, index) => (
        <Typography
          key={`${index}-${option.value}`}
          variant="caption"
          className={styles.chip}
        >
          {option.label ?? option.value}
        </Typography>
      ))}
      {hidden > 0 && (
        <Typography variant="caption" className={styles.chip}>
          {intl.formatMessage(messages.moreOptions, { count: hidden })}
        </Typography>
      )}
    </div>
  );
};

export const OptionsEditor: FC<OptionsEditorProps> = ({
  options,
  readOnly,
  error,
  optionBanks,
  optionBankId,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onLinkBank,
  onUnlinkBank,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const bankMenuGuard = useMenuEscapeGuard();

  if (readOnly) {
    return (
      <div>
        <Typography variant="body2" color="textSecondary">
          {intl.formatMessage(messages.options)}
        </Typography>
        <OptionChips options={options} />
        <Typography variant="caption" color="textSecondary">
          {intl.formatMessage(messages.standardOptionsNote)}
        </Typography>
      </div>
    );
  }

  if (optionBankId) {
    const bank = optionBanks.find(candidate => candidate.id === optionBankId);
    return (
      <div>
        <Typography variant="body2" color="textSecondary">
          {intl.formatMessage(messages.options)}
        </Typography>
        {bank && (
          <OptionChips
            options={bank.options.map(option => ({
              value: option.value ?? '',
              label: option.label,
            }))}
          />
        )}
        <div className={styles.bankRow}>
          <Typography variant="caption" color="textSecondary">
            {bank
              ? intl.formatMessage(messages.linkedOptionBank, {
                  name: bank.name,
                })
              : intl.formatMessage(messages.optionBankNote)}
          </Typography>
          <Button
            type="secondary"
            size="small"
            autoWidth
            onClick={onUnlinkBank}
          >
            {intl.formatMessage(messages.unlinkOptionBank)}
          </Button>
        </div>
        {bank && (
          <Typography variant="caption" color="textSecondary">
            {intl.formatMessage(messages.optionBankNote)}
          </Typography>
        )}
      </div>
    );
  }

  const bankChoices = optionBanks.map(bank => ({
    value: bank.id,
    label: bank.name,
  }));

  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {intl.formatMessage(messages.options)}
      </Typography>
      <div className={styles.optionsList}>
        {options.map((option, index) => {
          const visible = option.visible ?? true;
          const hasLabel = option.label !== undefined;
          return (
            <div
              className={cx(styles.optionRow, {
                [styles.optionRowHidden]: !visible,
              })}
              key={index}
            >
              {hasLabel && (
                <Input
                  size="small"
                  rootClass={styles.optionInput}
                  value={option.label ?? ''}
                  placeholder={intl.formatMessage(
                    messages.optionLabelPlaceholder,
                    { number: index + 1 },
                  )}
                  inputProps={{
                    'aria-label': intl.formatMessage(
                      messages.optionLabelPlaceholder,
                      { number: index + 1 },
                    ),
                  }}
                  onChange={event =>
                    onUpdateOption(index, { label: event.target.value })
                  }
                />
              )}
              <Input
                size="small"
                rootClass={styles.optionInput}
                value={option.value}
                placeholder={intl.formatMessage(messages.optionPlaceholder, {
                  number: index + 1,
                })}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.optionPlaceholder, {
                    number: index + 1,
                  }),
                }}
                onChange={event =>
                  onUpdateOption(index, { value: event.target.value })
                }
              />
              <IconButton
                size="small"
                className={styles.removeButton}
                aria-label={intl.formatMessage(
                  visible ? messages.hideOption : messages.showOption,
                )}
                aria-pressed={!visible}
                onClick={() => onUpdateOption(index, { visible: !visible })}
              >
                <Icon
                  icon={visible ? EyeOutlineIcon : EyeOffOutlineIcon}
                  size="xs"
                  color="inherit"
                />
              </IconButton>
              <IconButton
                size="small"
                className={styles.removeButton}
                aria-label={intl.formatMessage(messages.removeOption)}
                onClick={() => onRemoveOption(index)}
              >
                <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
              </IconButton>
            </div>
          );
        })}
        {error && (
          <Typography variant="caption" className={styles.helperError}>
            {error}
          </Typography>
        )}
        <div className={styles.optionsActions}>
          <Button type="secondary" size="small" autoWidth onClick={onAddOption}>
            {intl.formatMessage(messages.addOption)}
          </Button>
          {bankChoices.length > 0 && (
            <div className={styles.bankSelect}>
              <StyledReactSelect
                size="small"
                aria-label={intl.formatMessage(messages.linkOptionBank)}
                placeholder={intl.formatMessage(messages.linkOptionBank)}
                options={bankChoices}
                value={null}
                {...bankMenuGuard}
                onChange={(choice: { value: number }) =>
                  onLinkBank(choice.value)
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
